import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AppResponse } from '../../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../../core/services';

@Component({
    selector: 'lib-pending-approval-dialog-view',
    templateUrl: './pending-approval-dialog-view.component.html',
    styleUrls: ['./pending-approval-dialog-view.component.scss'],
})
export class PendingApprovalDialogViewComponent implements OnInit, OnDestroy {
    public isLoading: boolean = false;
    public isSubmit: boolean = false;
    public isShowButton: boolean = false;

    private subscribers: Subscription[] = [];

    constructor(
        private dialogRef: MatDialogRef<PendingApprovalDialogViewComponent>,
        private apiAdapterService: ApiAdapterServiceService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.getCampaignDetailService();

        this.isShowButton = this.getCampaignStorage?.generalInformation?.isShowButton ?? false;
    }

    get getCampaignStorage(): any {
        return localStorage.getItem('telin.campaign')
            ? JSON.parse(localStorage.getItem('telin.campaign'))
            : {};
    }

    private convertUTCDateToLocalDate(date: Date) {
        const newDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
        return newDate;
    }

    private getCampaignDetailService(): void {
        if (!this.getCampaignStorage?.generalInformation?.id) return;

        this.isLoading = true;

        const subs = this.apiAdapterService
            .executeApiRequest(
                [],
                `/campaign/${this.getCampaignStorage?.generalInformation?.id}`,
                'GET'
            )
            .subscribe(
                (resp: AppResponse<any>) => {
                    if (!resp) return;

                    const generalInformation = {
                        campaignName: resp.data?.name,
                        medium: resp.data?.platform,
                        channel: ['image', 'video', 'document'].includes(resp.data?.type)
                            ? 'MEDIA'
                            : resp.data?.isDirectSend
                              ? 'utility'
                              : resp.data?.type?.toUpperCase(),
                        media: resp.data?.type?.toUpperCase(),
                        isPrevTemplate: resp?.data?.isPrevTemplate,
                        templateName: resp?.data?.templateName,
                        ttl: resp?.data?.ttlSeconds,
                        from: {
                            id: resp.data?.accountId,
                            name: resp.data?.accountName,
                            platform: resp.data?.platform,
                            clientId: resp.data?.clientId,
                            clientName: resp.data?.clientName,
                            accountNo: resp.data?.accountNo,
                            wabaId: resp.data?.wabaId ?? '',
                        },
                        text: resp.data?.details?.text,
                        targetURL:
                            resp.data?.details?.image?.url ??
                            resp.data?.details?.document?.url ??
                            resp.data?.details?.video?.url ??
                            resp.data?.details?.template?.headerURL,
                        paramHeader: resp.data?.details?.template?.header,
                        paramBody: resp.data?.details?.template?.data,
                        paramButton: resp.data?.details?.template?.button,
                        paramCarousel:
                            resp.data?.details?.template?.carousel?.map((card: any) => {
                                return {
                                    headerURL: card.headerURL || '',
                                    bodyParams: (card.body || []).map((val: string) => ({
                                        paramName: val,
                                    })),
                                    buttonParams: (card.button || []).map((btnGroup: any[]) => {
                                        return (btnGroup || []).map((val: string) => ({
                                            paramName: val,
                                            payload: val,
                                        }));
                                    }),
                                };
                            }) || [],
                        otpValue: [...(resp.data?.details?.template?.data ?? [])]?.pop(),
                        variables: (resp.data?.details?.template?.data || []).map(
                            (val: string) => ({
                                paramName: val,
                            })
                        ),
                        isUseExistingTemplate: !!resp.data?.templateName,
                        template: {
                            ID: resp.data?.details?.template?.id,
                            name: resp.data?.details?.template?.name,
                            lang: resp.data?.details?.template?.lang,
                            platform: resp.data?.platform,
                            headerURL: resp.data?.details?.template?.headerURL,
                            header: {
                                examples: resp.data?.details?.template?.header,
                            },
                            body: {
                                examples: resp.data?.details?.template?.data,
                            },
                            buttons: resp.data?.details?.template?.button,
                            ClientId: resp.data?.clientId,
                            ClientName: resp.data?.clientName,
                        },
                        isDownloadVariableXLS: resp.data?.fileId !== '',
                        isUpload: resp.data?.fileId !== '',
                        fileId: resp.data?.fileId,
                    };

                    localStorage.setItem(
                        'telin.campaign',
                        JSON.stringify({
                            ...this.getCampaignStorage,
                            ...{
                                generalInformation: {
                                    id: resp.data?.id,
                                    form: generalInformation,
                                },
                                failover: resp.data?.failover,
                                targetAudience: resp.data?.targets,
                                targetAudienceFile: {
                                    name: resp.data?.fileName,
                                    targetURL: resp.data?.targetURL,
                                },
                                scheduler: {
                                    schedulerType:
                                        resp.data?.scheduleType === 'MULTI'
                                            ? 'ONE_TIME'
                                            : resp.data?.scheduleType,
                                    startDate: resp.data?.startTime?.split(' ')[0],
                                    startTime: resp.data?.startTime?.split(' ')[1],
                                    isRepeat: resp.data?.scheduleType === 'MULTI',
                                    delivery: resp.data?.repeat,
                                },
                            },
                        })
                    );

                    this.isLoading = false;
                },
                () => {
                    this.isLoading = false;
                }
            );

        this.subscribers.push(subs);
    }

    public rejectCampaignService(): void {
        this.isSubmit = true;

        const subs = this.apiAdapterService
            .executeApiRequest(
                [],
                `/campaign/${this.getCampaignStorage?.generalInformation?.id}/reject`,
                'POST'
            )
            .subscribe(
                (resp: AppResponse<any>) => {
                    this.isSubmit = false;

                    if (!resp || !resp?.data) return this.toastr.error('Error Rejecting Campaign!');

                    this.toastr.success('Campaign Rejected Successfully!');
                    this.onClose();
                },
                () => {
                    this.toastr.error('Error Rejecting Campaign!');
                    this.isSubmit = false;
                }
            );

        this.subscribers.push(subs);
    }

    public approveCampaignService(): void {
        this.isSubmit = true;

        const subs = this.apiAdapterService
            .executeApiRequest(
                [],
                `/campaign/${this.getCampaignStorage?.generalInformation?.id}/approve`,
                'POST'
            )
            .subscribe(
                (resp: AppResponse<any>) => {
                    this.isSubmit = false;

                    if (!resp || !resp?.data) return this.toastr.error('Error Approving Campaign!');

                    this.toastr.success('Campaign Approved Successfully!');
                    this.onClose();
                },
                () => {
                    this.toastr.error('Error Approving Campaign!');
                    this.isSubmit = false;
                }
            );

        this.subscribers.push(subs);
    }

    public onClose(): void {
        localStorage.removeItem('telin.campaign');
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.subscribers.forEach((each) => each.unsubscribe());
    }
}
